import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  ArrowRight, Heart, Gift, Home, Star, 
  Users, Crown, Sparkles, Eye, Moon
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import UniversalHeartToggle from './UniversalHeartToggle';

const GlitchyReInnLanding = ({ onEnterAsGiveth, onEnterAsGet, onNavigateToSecretHistory }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [showTerminalBreakthrough, setShowTerminalBreakthrough] = useState(false);
  const [mairyTerminal, setMairyTerminal] = useState('');
  const [terminalStep, setTerminalStep] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [burnThroughEffect, setBurnThroughEffect] = useState(0);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.3) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Trigger terminal breakthrough after 5 seconds (time to read)
  useEffect(() => {
    const breakthroughTimer = setTimeout(() => {
      setShowTerminalBreakthrough(true);
      
      // Burn through effect
      let intensity = 0;
      const burnInterval = setInterval(() => {
        setBurnThroughEffect(intensity);
        intensity += 0.1;
        if (intensity >= 1) {
          clearInterval(burnInterval);
        }
      }, 100);
    }, 5000); // 5 seconds to read copy
    
    return () => clearTimeout(breakthroughTimer);
  }, []);

  // M.ai.ry terminal sequence - Updated copy
  const terminalSequence = [
    "M.ai.ry Digital Innkeeper connecting...",
    "",
    "Hello. Welcome to the Guild of Families.",
    "",
    "We are a family and faith focused mutual aid society -",
    "a planetary federation where families support each other,", 
    "particularly through life's great transitions.",
    "",
    "Like AirBNB for care and belonging.",
    "The reunification of families during teachable moments.",
    "",
    "What brings your family here today?",
    "",
    "[SELECT YOUR PATH]"
  ];

  useEffect(() => {
    if (showTerminalBreakthrough && terminalStep < terminalSequence.length) {
      const timer = setTimeout(() => {
        setMairyTerminal(prev => prev + terminalSequence[terminalStep] + '\n');
        setTerminalStep(prev => prev + 1);
      }, 400);
      
      return () => clearTimeout(timer);
    }
  }, [showTerminalBreakthrough, terminalStep]);

  const createTerminalButton = (text, onClick, color = '#ff1493') => (
    <button
      onClick={onClick}
      className="border-2 px-6 py-3 font-mono text-sm transition-all hover:scale-105 hover:glow"
      style={{
        borderColor: color,
        backgroundColor: `${color}30`,
        color: color,
        boxShadow: `0 0 20px ${color}60`,
        textShadow: `0 0 10px ${color}`
      }}
    >
      {text}
    </button>
  );

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      {/* Normal Website Background - Always Visible */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/y2urskim_ChatGPT%20Image%20Sep%2027%2C%202025%20at%2001_20_24%20AM.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Grant Spiral - Always Present */}
      <div className="fixed inset-0 pointer-events-none opacity-20">
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(12) rotate(${spiralPhase}deg)`,
            filter: 'blur(2px) brightness(1.6) saturate(1.8)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-black/50" />

      {/* Normal Website Content - Always Visible */}
      <div className="relative z-10 flex flex-col min-h-screen">
        
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="text-center max-w-4xl">
            
            {/* re-Inn Title */}
            <h1 
              className="text-6xl md:text-8xl font-bold leading-tight tracking-wider mb-8"
              style={{
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 60px rgba(255, 215, 0, 1.2), 0 0 120px rgba(255, 140, 0, 0.8)',
                letterSpacing: '0.15em',
                color: '#F4D03F',
                filter: 'brightness(1.4)'
              }}
            >
              re-Inn
            </h1>
            
            <p 
              className="text-xl md:text-2xl font-bold tracking-widest mb-12"
              style={{
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 30px rgba(210, 180, 140, 1.1)',
                letterSpacing: '0.2em',
                color: '#E8D5B7',
                filter: 'brightness(1.3)'
              }}
            >
              THE LODGING WE'VE BEEN LONGING FOR
            </p>

            {/* Normal Website Description */}
            <div className="max-w-3xl mx-auto space-y-6 mb-12">
              <p 
                className="text-lg text-cyan-200 leading-relaxed"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                A private membership experience for families navigating life's sacred transitions.
                Extraordinary venues around the world paired with elite facilitators and place-makers.
              </p>
              
              <p 
                className="text-base text-yellow-100 leading-relaxed"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                From birth to death, honeymoon to wake, procreation to celebration — 
                re-Inn is the lodging of life's doula moments.
              </p>
            </div>

            {/* Normal Website Buttons - With error sounds */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <Button
                onClick={() => {
                  // Play light error noise
                  try {
                    const ctx = new (window.AudioContext || window.webkitAudioContext)();
                    const o = ctx.createOscillator();
                    const g = ctx.createGain();
                    o.type = "square";
                    o.frequency.value = 200;
                    o.connect(g);
                    g.connect(ctx.destination);
                    g.gain.setValueAtTime(0.1, ctx.currentTime);
                    g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
                    o.start();
                    o.stop(ctx.currentTime + 0.2);
                  } catch (e) {}
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-16 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <Gift className="w-6 h-6 mr-3" />
                Learn More
              </Button>
              
              <Button
                onClick={() => {
                  // Play light error noise
                  try {
                    const ctx = new (window.AudioContext || window.webkitAudioContext)();
                    const o = ctx.createOscillator();
                    const g = ctx.createGain();
                    o.type = "square";
                    o.frequency.value = 200;
                    o.connect(g);
                    g.connect(ctx.destination);
                    g.gain.setValueAtTime(0.1, ctx.currentTime);
                    g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
                    o.start();
                    o.stop(ctx.currentTime + 0.2);
                  } catch (e) {}
                }}
                className="bg-violet-600 hover:bg-violet-700 text-white h-16 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <Heart className="w-6 h-6 mr-3" />
                Apply for Membership
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom elements */}
        <div className="absolute bottom-4 right-4 z-20">
          <p 
            className="text-xs text-yellow-200/70"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            The Guild of Families • 508 Faith-Based Mutual Aid Society
          </p>
        </div>

        <div className="absolute bottom-4 left-4 z-20">
          <UniversalHeartToggle />
        </div>
      </div>

      {/* TERMINAL BREAKTHROUGH EFFECT */}
      {showTerminalBreakthrough && (
        <div 
          className="fixed inset-0 z-40 transition-all duration-1000"
          style={{
            background: `radial-gradient(circle at center, 
              rgba(255, 20, 147, ${burnThroughEffect * 0.1}) 0%, 
              rgba(0, 0, 17, ${burnThroughEffect * 0.7}) 40%, 
              rgba(0, 0, 0, ${burnThroughEffect * 0.9}) 70%,
              rgba(0, 0, 0, ${burnThroughEffect * 0.95}) 100%)`,
            backdropFilter: `blur(${burnThroughEffect * 8}px) brightness(${1 + burnThroughEffect * 0.5})`
          }}
        >
          
          {/* Burn-Through Visual Effects */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-pulse"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  width: `${Math.random() * 200 + 50}px`,
                  height: '2px',
                  background: `linear-gradient(90deg, transparent, #ff1493${Math.floor(Math.random() * 9)}0, transparent)`,
                  animationDelay: `${Math.random() * 3}s`,
                  transform: `rotate(${Math.random() * 360}deg)`
                }}
              />
            ))}
          </div>

          {/* M.ai.ry Terminal - Burning Through */}
          <div className="relative z-10 p-8 flex items-center justify-center min-h-screen">
            <div 
              className="max-w-4xl w-full transition-all duration-1000"
              style={{
                transform: `scale(${0.8 + burnThroughEffect * 0.3})`,
                filter: `brightness(${1 + burnThroughEffect * 0.5}) saturate(${1 + burnThroughEffect * 0.8})`
              }}
            >
              
              {/* Glitchy Terminal Window */}
              <div 
                className="bg-black/90 backdrop-blur-xl border-4 rounded-lg overflow-hidden transition-all duration-500"
                style={{
                  borderColor: '#ff1493',
                  boxShadow: `0 0 ${40 + burnThroughEffect * 60}px #ff1493, inset 0 0 40px #ff149320`,
                  animation: 'terminalGlow 2s ease-in-out infinite alternate'
                }}
              >
                {/* Terminal Header */}
                <div 
                  className="px-4 py-2 flex items-center space-x-2"
                  style={{ backgroundColor: '#ff1493' }}
                >
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                  <div className="w-3 h-3 bg-white/70 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                  <div className="w-3 h-3 bg-white/50 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                  <span className="text-white font-mono text-sm ml-4">M.ai.ry@guild-of-families:~</span>
                </div>
                
                {/* Terminal Content - Scrollable */}
                <div className="p-4 md:p-6 font-mono text-sm max-h-[55vh] overflow-y-auto">
                  <pre 
                    className="whitespace-pre-wrap leading-relaxed mb-6"
                    style={{ 
                      color: '#ff1493',
                      textShadow: '0 0 10px #ff1493',
                      fontFamily: 'Courier New, monospace'
                    }}
                  >
                    {mairyTerminal}
                    <span className="animate-pulse">█</span>
                  </pre>
                  
                  {terminalStep >= terminalSequence.length && (
                    <div className="space-y-6">
                      
                      {/* Terminal Buttons - Simplified */}
                      <div className="flex flex-col md:flex-row gap-4 mb-6">
                        {createTerminalButton('GIVE SUPPORT', onEnterAsGiveth)}
                        {createTerminalButton('NEED SUPPORT', onEnterAsGet, '#00ffff')}
                        {createTerminalButton('SECRET HISTORY', onNavigateToSecretHistory, '#ffff00')}
                      </div>
                      
                      {/* User Input Area */}
                      <div className="border-2 p-4 rounded" style={{ borderColor: '#ff1493', backgroundColor: '#ff149310' }}>
                        <div className="flex items-center space-x-2">
                          <span style={{ color: '#ff1493' }}>guild@families:~$</span>
                          <input
                            type="text"
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && userInput.trim()) {
                                if (userInput.toLowerCase().includes('giveth') || userInput.toLowerCase().includes('gift')) {
                                  onEnterAsGiveth();
                                } else if (userInput.toLowerCase().includes('get') || userInput.toLowerCase().includes('council')) {
                                  onEnterAsGet();
                                } else {
                                  setMairyTerminal(prev => prev + `\n> "${userInput}" RECEIVED\n> PROCESSING FAMILY CONSTELLATION...\n> TYPE 'giveth' OR 'council' TO CONTINUE\n`);
                                  setUserInput('');
                                }
                              }
                            }}
                            className="flex-1 bg-transparent outline-none font-mono"
                            style={{ color: '#ffffff' }}
                            placeholder="TYPE YOUR RESPONSE..."
                          />
                          <span className="animate-pulse" style={{ color: '#ff1493' }}>█</span>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-xs leading-relaxed" style={{ color: '#ff1493' }}>
                          GUILD OF FAMILIES • FAITH: PROCREATION OF LOVE FOR 10,000 GENERATIONS<br/>
                          PRACTICE: DEEPEN AND FLOURISH WITHIN YOUR OWN FAITH
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for Terminal Glow Effect */}
      <style jsx>{`
        @keyframes terminalGlow {
          0% { box-shadow: 0 0 40px #ff1493, inset 0 0 40px #ff149320; }
          100% { box-shadow: 0 0 80px #ff1493, inset 0 0 60px #ff149340; }
        }
        .hover\\:glow:hover {
          filter: brightness(1.3) saturate(1.5);
        }
      `}</style>
    </div>
  );
};

export default GlitchyReInnLanding;